import { detectDateCadence } from './detectDateCadence.ts';
import { matchesAnyKeyword } from './matchesAnyKeyword.ts';
import { matchesStatusUpdateTemplate } from './matchesStatusUpdateTemplate.ts';
import { parseTitleDate } from './parseTitleDate.ts';
import {
	RECURRENCE_INDICATOR_KEYWORDS,
	STATUS_UPDATE_PARENT_KEYWORDS,
	STATUS_UPDATE_TITLE_KEYWORDS,
	STATUS_UPDATE_WORD_KEYWORDS,
} from './statusUpdateSignalKeywords.ts';

/** A child page/folder of the current content, as known by the caller. */
export type StatusUpdateChild = {
	title: string;
	createdAt: string | number | Date;
};

export type StatusUpdateSignalInput = {
	title?: string;
	parentTitle: string | null;
	templateId: string | null;
	children?: readonly StatusUpdateChild[];
};

export type StatusUpdateSignalMatch = {
	matches: boolean;
	matchedTitle: boolean;
	matchedChildren: boolean;
	matchedTemplate: boolean;
	matchedParentTitle: boolean;
};

/**
 * A recurring status-update title pairs a recurrence indicator (a cadence word,
 * "weekly", "monthly", "quarterly", "yearly") with an update word ("update",
 * "status", "recap", "report") — e.g. "Aug update", "weekly report".
 */
const isRecurringUpdateTitle = (title: string | undefined): boolean =>
	matchesAnyKeyword(title, RECURRENCE_INDICATOR_KEYWORDS) &&
	matchesAnyKeyword(title, STATUS_UPDATE_WORD_KEYWORDS);

/**
 * Signals a recurring status-update series from a set of children.
 * A child only counts when its title uses an update word ("update", "status",
 * "recap", "report")
 */
const doesChildrenLookLikeSeries = (
	children: readonly StatusUpdateChild[] | undefined,
): boolean => {
	if (!children || children.length === 0) {
		return false;
	}

	// Only consider children with an update-related keyword in their title.
	const statusUpdateRelatedKeywords = STATUS_UPDATE_WORD_KEYWORDS;
	const childrenWithUpdateRelatedKeyword = children.filter((child) =>
		matchesAnyKeyword(child.title, statusUpdateRelatedKeywords),
	);
	if (childrenWithUpdateRelatedKeyword.length === 0) {
		return false;
	}

	const titleDates: number[] = [];
	const creationDates: (string | number | Date)[] = [];
	for (const child of childrenWithUpdateRelatedKeyword) {
		creationDates.push(child.createdAt);
		const titleDate = parseTitleDate(child.title);
		if (titleDate !== undefined) {
			titleDates.push(titleDate);
		}
	}

	return Boolean(detectDateCadence(creationDates)) || Boolean(detectDateCadence(titleDates));
};

/**
 * Returns each signal that makes the page look like a status update.
 */
export const getStatusUpdateSignalMatch = (
	input: StatusUpdateSignalInput,
): StatusUpdateSignalMatch => {
	const { title, parentTitle } = input;

	const matchedTitle =
		matchesAnyKeyword(title, STATUS_UPDATE_TITLE_KEYWORDS) || isRecurringUpdateTitle(title);
	const matchedChildren = doesChildrenLookLikeSeries(input.children);
	const matchedTemplate = matchesStatusUpdateTemplate(input.templateId);
	const matchedParentTitle = matchesAnyKeyword(parentTitle, STATUS_UPDATE_PARENT_KEYWORDS);

	return {
		matches: matchedTitle || matchedChildren || matchedTemplate || matchedParentTitle,
		matchedTitle,
		matchedChildren,
		matchedTemplate,
		matchedParentTitle,
	};
};
